import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useContext, useEffect, useState } from 'react';
import { AlumnoType, PayloadAlumnoType } from '../../types/types';
import { Alert, Chip, CircularProgress, FormControl, FormControlLabel, FormHelperText, Grid, InputLabel, List, MenuItem, Paper, Select, SelectChangeEvent, Switch } from '@mui/material';
import { usePostAlumno } from '../../hooks/usePostAlumno';
import dayjs from 'dayjs';
import { UserContext } from '../../contexts/userContext';
import { UseGetHnos, UseRemoveHnos } from '../../hooks/hermanos';
import AgregarHermanos from './AgregarHno';
import { IndicatorButton } from '../IndicatorButton';
import { useGetGrupos } from '../../hooks/useGrupos';
import { getCategoria, getNivelOptions } from '../../utils/utils';

export interface AlumnosDetailProps {
  alumno?: PayloadAlumnoType;
  open: boolean;
  setOpen: (open:boolean) => void;
  onClose: () => void;
  refetchLista: () => void;
  lista: AlumnoType[];
}

export const dateFormat = 'DD/MM/YYYY';

export default function AlumnosDetail(props:AlumnosDetailProps) {
  const { alumno, open, onClose, setOpen, refetchLista, lista } = props;
  const [ mutableAlumno, setMutableAlumno ] = useState<PayloadAlumnoType | undefined>();
  const [estaEditando, setEstaEditando] = useState(!alumno);
  const [agregarHnos, setAgregarHnos] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useContext(UserContext);
  const alumnoApiCall = usePostAlumno({ alumno: mutableAlumno }, user );
  const hermanos = UseGetHnos(alumno?._id, user);
  const [removeHno, setRemoveHno] = useState<string>();
  const removeHnoResp = UseRemoveHnos(removeHno, user);
  const grupos = useGetGrupos(user);

  const nivelesOptions = getNivelOptions(mutableAlumno);

  useEffect(() => {
    if (removeHnoResp.data) hermanos.refetch();
  }, [removeHnoResp.data]);

  useEffect(() =>{
    if (alumno) {
      if (alumno?.fechanac
        && alumno.fechanac.length > 10
        && dayjs(alumno.fechanac).isValid()) alumno.fechanac = dayjs(alumno.fechanac).format(dateFormat);
      else if (dayjs(alumno.fechanac, dateFormat).isValid()) alumno.fechanac = dayjs(alumno.fechanac, dateFormat).format(dateFormat);

      if (alumno?.fechains
        && alumno.fechains.length > 10
        && dayjs(alumno.fechains).isValid()) alumno.fechains = dayjs(alumno.fechains).format(dateFormat);
      else if (dayjs(alumno.fechains, dateFormat).isValid()) alumno.fechains = dayjs(alumno.fechains, dateFormat).format(dateFormat);

    }
    // si es alumno nuevo poner fechas actuales en campos de fecha
    if (estaEditando && !alumno) {
      setMutableAlumno({ fechains: dayjs().format(dateFormat), fechanac: dayjs().format(dateFormat) });
    } else {
      setMutableAlumno(alumno);
    }
    setEstaEditando(!alumno);
  }, [alumno, open]);

  if (!user?._id) return null;


  let titulo = '';
  if (estaEditando && alumno) titulo = 'Editar Alumno';
  if (estaEditando && !alumno) titulo = 'Crear Nuevo Alumno';
  if (!estaEditando && alumno) titulo = 'Ver detalles de alumno';

  const validarDni = (dni?: string) => {
    if (!dni || dni.length === 0) return { valido: false, mensaje: 'El DNI no puede quedar vacio' };
    const dniYaExiste = lista.filter((a) => a.dni === mutableAlumno?.dni).length > 0 && (estaEditando && !alumno);
    if (dniYaExiste) return { valido: false, mensaje: 'Ya existe un alumno con este DNI' };
    const formatoInvalido = (dni.length > 9 || dni.length < 7 || !/^\d+$/.test(dni) );
    let allAreSame = true;
    for (let i = 0; i < dni.toString().length; i++) {
      if (i > 0 && dni.toString()[i] !== dni.toString()[i - 1]) {
        allAreSame = false;
      }
    }
    if (allAreSame || formatoInvalido ) return { valido: false, mensaje: 'Formato Invalido' };
    return { valido: true, mensaje: '' };
  };

  const descartar = () => {
    setMutableAlumno(undefined);
    setOpen(false);
    setEstaEditando(!alumno);
  };

  const handleSubmit = () => {
    setLoading(true);
    alumnoApiCall.refetch();
    setTimeout(() => {
      setEstaEditando(!alumno);
      refetchLista();
      setMutableAlumno(undefined);
      setLoading(false);
      onClose();
    }, 3000);
  };
  //(estaEditando && !alumno) = Esta creando nuevo

  return (
    <div>
      <Dialog open={open} onClose={estaEditando ? ()=>{} : onClose} closeAfterTransition >
        <DialogTitle>{titulo}</DialogTitle>
        {mutableAlumno?.inactivo &&
            <Alert severity="warning">Si marcas este alumno como inactivo no lo veras en la lista a menos que actives la vista de alumnos inactivos.</Alert>
        }
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Switch
                  color="error"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setMutableAlumno((prev) => ({ ...prev, inactivo: event.target.checked }));
                  }}
                />}
                disabled={!estaEditando}
                checked={mutableAlumno?.inactivo}
                label="Inactivo"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Switch
                  color="primary"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setMutableAlumno((prev) => ({ ...prev, descuentoHermano: event.target.checked }));
                  }}
                />}
                disabled={!estaEditando}
                checked={mutableAlumno?.descuentoHermano}
                label="Descuento por hermano"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Switch
                  color="primary"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setMutableAlumno((prev) => ({ ...prev, becado: event.target.checked }));
                  }}
                />}
                disabled={!estaEditando}
                checked={mutableAlumno?.becado}
                label="Becado"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                id="name"
                label="Nombre"
                type="text"
                fullWidth
                variant="standard"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMutableAlumno((prev) => ({ ...prev, name: (event.target as HTMLInputElement).value as string }));
                }}
                value={mutableAlumno?.name ?? ''}
                inputProps={{ readOnly: !estaEditando }}
                required
                error={!mutableAlumno?.name}
                helperText={!mutableAlumno?.name ? 'El Nombre no puede quedar vacio' : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                id="apellido"
                label="Apellido"
                type="text"
                fullWidth
                variant="standard"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMutableAlumno((prev) => ({ ...prev, apellido: (event.target as HTMLInputElement).value as string }));
                }}
                value={mutableAlumno?.apellido ?? ''}
                inputProps={{ readOnly: !estaEditando }}
                required
                error={!mutableAlumno?.apellido}
                helperText={!mutableAlumno?.apellido ? 'El Apellido no puede quedar vacio' : ''}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                margin="dense"
                id="dni"
                label="DNI"
                type="number"
                fullWidth
                variant="standard"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (!event.target.value &&  mutableAlumno?.dni && mutableAlumno.dni.length > 1) return;
                  setMutableAlumno((prev) => {
                    const fixed = (event.target.value).toString().replace(/\D/g, '');
                    return ({ ...prev, dni: fixed });
                  });
                }}
                required
                value={mutableAlumno?.dni ?? ''}
                inputProps={{ readOnly: !estaEditando }}
                error={!validarDni(mutableAlumno?.dni).valido}
                helperText={!validarDni(mutableAlumno?.dni).valido ? validarDni(mutableAlumno?.dni).mensaje : ''}
                sx={{
                  'input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
                    WebkitAppearance: 'none',
                    margin: 0,
                  },
                  'input[type=number]': {
                    MozAppearance: 'textfield',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                margin="dense"
                id="fechanac"
                label="Fecha de Nacimiento"
                type="text"
                fullWidth
                variant="standard"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMutableAlumno((prev) => ({ ...prev, fechanac: event.target.value }));
                }}
                value={loading ? dayjs(mutableAlumno?.fechanac).format(dateFormat) : mutableAlumno?.fechanac || ''}
                inputProps={{ readOnly: !estaEditando }}
                error={!loading && !dayjs(mutableAlumno?.fechanac, dateFormat, true).isValid()}
                helperText={!loading && !dayjs(mutableAlumno?.fechanac, dateFormat, true).isValid() ? 'Formato de fecha invalido' : ''}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                margin="dense"
                id="fechains"
                label="Fecha de Inscripcion"
                type="text"
                fullWidth
                variant="standard"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMutableAlumno((prev) => ({ ...prev, fechains: event.target.value }));
                }}
                value={loading ? dayjs(mutableAlumno?.fechains).format(dateFormat) : mutableAlumno?.fechains || ''}
                inputProps={{ readOnly: !estaEditando }}
                error={!loading && !dayjs(mutableAlumno?.fechains, dateFormat, true ).isValid()}
                helperText={!loading && !dayjs(mutableAlumno?.fechains, dateFormat, true).isValid() ? 'Formato de fecha invalido' : ''}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                id="celpapa"
                label="Cel Padre"
                type="text"
                fullWidth
                variant="standard"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMutableAlumno((prev) => ({ ...prev, celpapa: (event.target as HTMLInputElement).value as string }));
                }}
                value={mutableAlumno?.celpapa ?? ''}
                inputProps={{ readOnly: !estaEditando }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                id="celmama"
                label="Cel Madre"
                type="text"
                fullWidth
                variant="standard"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMutableAlumno((prev) => ({ ...prev, celmama: (event.target as HTMLInputElement).value as string }));
                }}
                value={mutableAlumno?.celmama ?? ''}
                inputProps={{ readOnly: !estaEditando }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                margin="dense"
                id="direccion"
                label="Direccion"
                type="text"
                fullWidth
                variant="standard"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMutableAlumno((prev) => ({ ...prev, direccion: (event.target as HTMLInputElement).value as string }));
                }}
                value={mutableAlumno?.direccion ?? ''}
                inputProps={{ readOnly: !estaEditando }}
              />
            </Grid>
            <Grid item xs={12} md={6} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', paddingBottom: 4 }}>
              <FormControl>
                <InputLabel>Grupo</InputLabel>
                <Select
                  id="selector-grupo"
                  label="Grupo"
                  variant='standard'
                  value={mutableAlumno?.grupo ?? ''}
                  onChange={(event: SelectChangeEvent) => {
                    setMutableAlumno((prev) => ({ ...prev, grupo: event.target.value }));
                  }}
                  readOnly={!estaEditando}
                >
                  <MenuItem key={'sin-grupo'} value={''}>Sin Grupo Asignado</MenuItem>
                  {grupos.data?.map((grupo) => (
                    <MenuItem key={grupo._id} value={grupo.nombre}>{grupo.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', paddingBottom: 4, paddingTop: 50 }}>
              <FormControl>
                <InputLabel>Genero</InputLabel>
                <Select
                  id="selector-genero"
                  label="Grupo"
                  variant='standard'
                  value={mutableAlumno?.genero ?? ''}
                  onChange={(event: SelectChangeEvent) => {
                    setMutableAlumno((prev) => ({ ...prev, genero: event.target.value as 'M' | 'F' }));
                  }}
                  readOnly={!estaEditando}
                >
                  <MenuItem key={'masculino'} value={'M'}>Masculino</MenuItem>
                  <MenuItem key={'femenina'} value={'F'}>Femenina</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={3} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', paddingBottom: 4, paddingTop: 50  }}>
              <FormControlLabel
                control={<Switch
                  color="primary"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setMutableAlumno((prev) => ({ ...prev, federado: event.target.checked }));
                  }}
                />}
                disabled={!estaEditando}
                checked={mutableAlumno?.federado}
                label="Federado"
              />
            </Grid>
            <Grid item xs={6} md={3} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', paddingBottom: 4, paddingTop: 50  }}>
              <TextField
                id="categoria"
                label="Categoria"
                variant='standard'
                value={mutableAlumno?.categoria ?? getCategoria(mutableAlumno) ?? ''}
                InputProps={{ readOnly: true }}
                helperText={mutableAlumno?.genero ? undefined : 'Faltan genero'}
                error={!mutableAlumno?.genero}
              />
            </Grid>
            <Grid item xs={6} md={3} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', paddingBottom: 4, paddingTop: 50  }}>
              <FormControl error={!mutableAlumno?.genero}>
                <InputLabel>Nivel</InputLabel>
                <Select
                  id="selector-nivel"
                  label="Nivel"
                  variant='standard'
                  value={mutableAlumno?.nivel ?? ''}
                  onChange={(event: SelectChangeEvent) => {
                    setMutableAlumno((prev) => ({ ...prev, nivel: event.target.value }));
                  }}
                  readOnly={!estaEditando || !nivelesOptions}
                >
                  {nivelesOptions?.map((nivel) => (
                    <MenuItem key={nivel} value={nivel}>{nivel}</MenuItem>
                  ))}
                </Select>
                {!mutableAlumno?.genero && <FormHelperText>Falta genero</FormHelperText>}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                id="obs"
                label="Observaciones"
                type="text"
                fullWidth
                variant="standard"
                multiline
                rows={4}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setMutableAlumno((prev) => ({ ...prev, obs: (event.target as HTMLInputElement).value as string }));
                }}
                value={mutableAlumno?.obs ?? ''}
                inputProps={{ readOnly: !estaEditando }}
              />
            </Grid>
            <Grid item xs={12}>
              {!hermanos.isLoading && hermanos.data?.length ?
                <Paper elevation={3} style={{ padding: 8, display: 'flex', flexDirection: 'column' }}>
                  {'Hermanos'}
                  <List>
                    {hermanos.data.map((h) => {
                      if (h === alumno?._id) return null;
                      const hermano = lista.find((hl) => hl._id === h);
                      if (hermano) return (
                        <Chip
                          key={hermano._id}
                          label={`${hermano.apellido} ${hermano.name}`}
                          onDelete={() => {setRemoveHno(hermano._id);}}
                          size="small"
                          style={{ margin: 4 }}
                        />
                      );
                      return null;
                    } )}
                  </List>
                  <Button style={{ marginLeft: 'auto' }} onClick={() => setAgregarHnos(true)}>
               Agregar Hermano
                  </Button>
                </Paper>
                : <Button onClick={() => setAgregarHnos(true)}>
                Agregar Hermano
                </Button>}
              {hermanos.isLoading && <CircularProgress />}
              {alumno?._id && hermanos.data && <AgregarHermanos
                open={agregarHnos}
                onCancell={() => setAgregarHnos(false)}
                onUpdate={() => {
                  hermanos.refetch();
                }}
                lista={lista}
                alumno={alumno._id}
                hermanos={hermanos.data}
              />}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {user?.roles?.includes('editar datos') && !estaEditando &&
          <>
            <Button
              variant="contained"
              onClick={() => setEstaEditando(true)}
            >
               Editar
            </Button>
            <Button
              variant="contained"
              onClick={() =>{
                setMutableAlumno(undefined);
                setEstaEditando(!alumno);
                onClose();
              }}
            >
                       Salir
            </Button>
          </>
          }
          {estaEditando &&
          <Button
            variant="contained"
            disabled={alumnoApiCall.isLoading || loading}
            onClick={() =>{
              setEstaEditando(false);
              if (alumno) setMutableAlumno(alumno);
              else descartar();
            }}
          >
               Descartar
          </Button>}
          {estaEditando &&
          <IndicatorButton
            label='Guardar Cambios'
            disabled={
              !mutableAlumno?.name
              || !mutableAlumno.apellido
              || alumnoApiCall.isLoading
              || !validarDni(mutableAlumno.dni).valido
              || !dayjs(mutableAlumno?.fechanac, dateFormat, true).isValid()
              || !dayjs(mutableAlumno?.fechains, dateFormat, true).isValid()
            }
            onClick={handleSubmit}
            loading={alumnoApiCall.isLoading || loading}
          />}
        </DialogActions>
      </Dialog>
    </div>
  );
}
